import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { tr, lbl } from "../lib/i18n";
import { AddLeadDialog } from "../components/AddLeadDialog";

const sel = "input h-10 w-full sm:w-auto";
const when = (at) => (at || "").slice(0, 16).replace("T", " ");

const LeadCard = ({ l }) => (
  <li data-testid={`lead-card-${l.id}`}>
    <Link to={`/leads/${l.id}`} className="block p-3 min-w-0" data-testid={`lead-card-link-${l.id}`}>
      <div className="flex items-start justify-between gap-2">
        <span className="font-medium break-words min-w-0">{l.customer_name}</span>
        <span className="text-xs text-muted-foreground shrink-0" dir="ltr">{when(l.created_at)}</span>
      </div>
      <div className="text-sm break-words">{l.vehicle_label || "—"}</div>
      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
        <span>{tr("ld.col_stage")}: <b className="font-medium text-foreground">{lbl(l.stage || "New")}</b></span>
        <span>{tr("ld.col_salesperson")}: <b className="font-medium text-foreground">{l.assigned_name || tr("ld.unassigned")}</b></span>
        <span>{tr("ld.col_source")}: <b className="font-medium text-foreground">{lbl(l.source)}</b></span>
        {!l.opened_at && l.assigned_to ? <span className="px-1.5 bg-amber-100 text-amber-900">{tr("ld.not_opened")}</span> : null}
      </div>
    </Link>
  </li>
);

export default function LeadsPage() {
  const [facets, setFacets] = useState(null);
  const [f, setF] = useState({ source: "", stage: "", assigned_to: "", q: "", today: false });
  const [data, setData] = useState({ items: [], total: 0 });
  const [adding, setAdding] = useState(false);
  const load = useCallback(async () => {
    try {
      const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
      const { data: d } = await api.get("/leads", { params });
      setData(d);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [f]);
  useEffect(() => { api.get("/leads/facets").then((r) => setFacets(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("branch-change", h);
    return () => window.removeEventListener("branch-change", h);
  }, [load]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  return (
    <div data-testid="leads-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div><div className="eyebrow">{tr("x.crm")}</div><h1 className="text-3xl font-semibold" style={{ fontFamily: "Outfit" }}>{tr("ld.title")}</h1></div>
        <button className="btn-primary" onClick={() => setAdding(true)} data-testid="add-lead-btn"><Plus size={16} /> {tr("ld.add")}</button>
      </div>
      <div className="flex flex-wrap gap-2 mb-4" data-testid="leads-filters">
        <input className="input h-10 w-full sm:w-64" placeholder={tr("ld.search")} value={f.q} onChange={set("q")} data-testid="leads-search" />
        <select className={sel} value={f.source} onChange={set("source")} data-testid="leads-filter-source">
          <option value="">{tr("ld.all_sources")}</option>
          {(facets?.sources || []).map((s) => <option key={s} value={s}>{lbl(s)}</option>)}
        </select>
        <select className={sel} value={f.stage} onChange={set("stage")} data-testid="leads-filter-stage">
          <option value="">{tr("ld.all_stages")}</option>
          {(facets?.stages || []).map((s) => <option key={s} value={s}>{lbl(s)}</option>)}
        </select>
        {facets?.can_assign && (
          <select className={sel} value={f.assigned_to} onChange={set("assigned_to")} data-testid="leads-filter-salesperson">
            <option value="">{tr("ld.all_staff")}</option>
            <option value="unassigned">{tr("ld.unassigned")}</option>
            {facets.staff.map((s) => <option key={s.id} value={s.id}>{s.full_name}</option>)}
          </select>
        )}
        <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={f.today} onChange={set("today")} data-testid="leads-filter-today" />{tr("ld.today")}</label>
      </div>
      <div className="text-sm text-muted-foreground mb-2" data-testid="leads-total">{tr("ld.count", { p0: data.total })}</div>
      <ul className="md:hidden border border-[var(--divider)] divide-y divide-[var(--divider)]" data-testid="leads-cards">
        {data.items.map((l) => <LeadCard key={l.id} l={l} />)}
        {!data.items.length && <li className="p-6 text-center text-muted-foreground" data-testid="leads-empty-mobile">{tr("ld.empty")}</li>}
      </ul>
      <div className="hidden md:block overflow-x-auto border border-[var(--divider)]">
        <table className="w-full text-sm" data-testid="leads-table">
          <thead><tr className="text-start">
            {["customer", "car", "source", "stage", "salesperson", "received"].map((k) => <th key={k} className="text-start p-3 font-medium">{tr(`ld.col_${k /* i18n-keys: customer,car,source,stage,salesperson,received */}`)}</th>)}
          </tr></thead>
          <tbody>
            {data.items.map((l) => (
              <tr key={l.id} className="border-t border-[var(--divider)] hover:bg-[var(--surface-2,#f6f6f4)]" data-testid={`lead-row-${l.id}`}>
                <td className="p-3"><Link to={`/leads/${l.id}`} className="font-medium underline-offset-2 hover:underline" data-testid={`lead-link-${l.id}`}>{l.customer_name}</Link>
                  {!l.opened_at && l.assigned_to ? <span className="ms-2 text-xs px-1.5 py-0.5 bg-amber-100 text-amber-900">{tr("ld.not_opened")}</span> : null}</td>
                <td className="p-3">{l.vehicle_label || "—"}</td>
                <td className="p-3">{lbl(l.source)}</td>
                <td className="p-3">{lbl(l.stage || "New")}</td>
                <td className="p-3">{l.assigned_name || tr("ld.unassigned")}</td>
                <td className="p-3 whitespace-nowrap" dir="ltr">{(l.created_at || "").slice(0, 16).replace("T", " ")}</td>
              </tr>
            ))}
            {!data.items.length && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground" data-testid="leads-empty">{tr("ld.empty")}</td></tr>}
          </tbody>
        </table>
      </div>
      {adding && <AddLeadDialog facets={facets} onClose={() => setAdding(false)} onDone={() => { setAdding(false); load(); }} />}
    </div>
  );
}
